import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { clientsApi, ApiError } from "@/api";
import type { ClientDetailResponse, ClientRecord, JobClient } from "@/types";
import { Button, Spinner, Pill, Toast, useToast } from "@/components/ui";
import { humanise } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { formatMoney } from "@/lib/money";
import { JobStagePill } from "../jobs/JobChips";
import { EditClientDrawer } from "../jobs/EditClientDrawer";
import "./clients.css";

/** Display name from a full client record (company → company name; else the
 *  trimmed individual name; else email; else "Client #id"). Mirrors the
 *  clients/list server logic so the hub title matches the list row. */
function displayName(c: ClientRecord): string {
  if (c.is_company && c.company_name) return c.company_name;
  const full = `${c.contact_first_name} ${c.contact_last_name}`.trim();
  if (full) return full;
  return c.email || `Client #${c.id}`;
}

/** Adapt a full client record to the JobClient shape the shared
 *  EditClientDrawer expects (is_lead/display are unused by the form). */
function asJobClient(c: ClientRecord): JobClient {
  return {
    id: c.id,
    is_lead: false,
    is_company: c.is_company,
    display: displayName(c),
    contact_first_name: c.contact_first_name,
    contact_last_name: c.contact_last_name,
    company_name: c.company_name,
    email: c.email,
    phone: c.phone,
    address_line_1: c.address_line_1,
    address_line_2: c.address_line_2,
    address_line_3: c.address_line_3,
    city: c.city,
    county: c.county,
    address: c.address,
    postcode: c.postcode,
    notes: c.notes,
  };
}

/** The client hub — one GET /clients/detail drives the contact card, invoice
 *  financials, linked jobs (with the lead marker), and open activity (tasks +
 *  applications, each linking back to its job). Edit reuses the shared
 *  client-edit drawer (api169 client_update); a save refetches the hub. */
export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);

  const [detail, setDetail] = useState<ClientDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const { toast, showToast } = useToast();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    clientsApi
      .getClientDetail(clientId)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the client.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => load(), [load]);

  if (loading) {
    return (
      <section className="client-detail">
        <Link to="/staff/clients" className="open-apps__back">← Clients</Link>
        <div className="client-detail__loading"><Spinner label="Loading client…" /></div>
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="client-detail">
        <Link to="/staff/clients" className="open-apps__back">← Clients</Link>
        <p className="leads__error" role="alert">{error || "Client not found."}</p>
      </section>
    );
  }

  const { client, jobs, open_tasks, open_applications, financials, xero_synced, portal } = detail;
  const name = displayName(client);
  // Address line(s) WITHOUT the postcode — it has its own row beneath. Prefer
  // the split fields; for a legacy single-string address, drop any comma
  // segment that's just the postcode so it isn't shown twice.
  const pc = client.postcode.trim().toUpperCase();
  const addressParts = [client.address_line_1, client.address_line_2, client.address_line_3, client.city, client.county].filter(Boolean);
  const composedAddress = addressParts.length
    ? addressParts.join(", ")
    : client.address
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s && s.toUpperCase() !== pc)
        .join(", ");

  // App access. There's no self-signup, so the portal_user row IS the invitation
  // and an email address is the one hard prerequisite for creating it.
  const clientEmail = client.email.trim();
  const portalState: "none" | "disabled" | "pending" | "active" = !portal.invited
    ? "none"
    : !portal.is_active
      ? "disabled"
      : portal.has_signed_in
        ? "active"
        : "pending";

  // Archive eligibility — mirrors the clients/archive server guard so the button
  // and its tooltip match what the endpoint will allow: every job archived, no
  // open tasks/applications, nothing outstanding/overdue. (The server re-checks.)
  const isArchived = client.status === "archived";
  const activeJobs = jobs.filter((j) => j.status !== "archived").length;
  const blockers: string[] = [];
  if (activeJobs > 0) blockers.push(`${activeJobs} active job${activeJobs === 1 ? "" : "s"}`);
  if (open_tasks.length > 0) blockers.push(`${open_tasks.length} open task${open_tasks.length === 1 ? "" : "s"}`);
  if (open_applications.length > 0)
    blockers.push(`${open_applications.length} open application${open_applications.length === 1 ? "" : "s"}`);
  if (financials.outstanding > 0 || financials.overdue > 0) blockers.push("outstanding invoices");
  const canArchive = blockers.length === 0;
  const archiveTitle = canArchive
    ? "Archive this client and archive its Xero contact."
    : `Can't archive yet — ${blockers.join(", ")}.`;

  async function archive() {
    if (archiving || isArchived || !canArchive) return;
    if (!window.confirm(`Archive ${name}? This archives the client and its Xero contact. You'll still find it via "Include archived" on the list.`)) return;
    setArchiving(true);
    setError(undefined);
    try {
      const res = await clientsApi.archiveClient(clientId);
      if (res.xero_archived) showToast("Client archived, and archived in Xero.");
      else if (res.xero_error === "no_xero_contact_id") showToast("Client archived. (No Xero contact to archive.)");
      else showToast("Client archived — but its Xero contact could not be archived.");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not archive the client.");
    } finally {
      setArchiving(false);
    }
  }

  // Invite (or re-invite) this client to the mobile app. The email carries only
  // the app download links — never a sign-in link, which would expire during the
  // App Store detour — so the client signs in by entering their address in the
  // app. Idempotent server-side, so this doubles as "resend".
  async function invite() {
    if (inviting || !clientEmail) return;
    const verb = portal.invited ? "Resend the invite to" : "Invite";
    if (!window.confirm(`${verb} ${clientEmail}? They'll get an email with the app download links.`)) return;
    setInviting(true);
    setError(undefined);
    try {
      const res = await clientsApi.invitePortalUser(clientId);
      // Access is granted even when the email can't go out (no invite template
      // configured in SendGrid) — say so rather than implying an email landed.
      if (res.email_sent) showToast(res.created ? `Invited — app links sent to ${res.email}.` : `Invite resent to ${res.email}.`);
      else if (res.created) showToast(`App access granted for ${res.email}, but no email was sent — the invite template isn't set up in SendGrid yet.`);
      else showToast(`${res.email} already has access. No email was sent — the invite template isn't set up in SendGrid yet.`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the invite.");
    } finally {
      setInviting(false);
    }
  }

  // Reopen sets the client back to active. Unlike archive it does NOT touch Xero,
  // so if the record has a Xero contact we remind the user to reactivate it there.
  async function reopen() {
    if (archiving || !isArchived) return;
    if (!window.confirm(`Reopen ${name}? This sets the client back to active.`)) return;
    setArchiving(true);
    setError(undefined);
    try {
      const res = await clientsApi.reopenClient(clientId);
      if (res.has_xero_contact)
        showToast("Client reopened. Remember to reactivate the contact in Xero manually.");
      else showToast("Client reopened.");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reopen the client.");
    } finally {
      setArchiving(false);
    }
  }

  return (
    <section className="client-detail">
      <Link to="/staff/clients" className="open-apps__back">← Clients</Link>

      <header className="client-detail__head">
        <div>
          <h1>{name}</h1>
          <p className="client-detail__tags">
            <span className={`clients-badge ${client.is_company ? "clients-badge--company" : "clients-badge--muted"}`}>
              {client.is_company ? "Company" : "Individual"}
            </span>
            <span className={`clients-badge ${xero_synced ? "clients-badge--ok" : "clients-badge--muted"}`}>
              {xero_synced ? "In Xero" : "Not in Xero"}
            </span>
            {client.status === "archived" ? <span className="clients-badge clients-badge--muted">Archived</span> : null}
          </p>
        </div>
        <div className="client-detail__actions">
          {isArchived ? (
            <Button
              variant="secondary"
              onClick={reopen}
              loading={archiving}
              disabled={archiving}
              title="Reopen this client (sets it back to active). Reactivate its Xero contact manually."
            >
              Reopen client
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={archive}
              loading={archiving}
              disabled={archiving || !canArchive}
              title={archiveTitle}
            >
              Archive client
            </Button>
          )}
        </div>
      </header>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <div className="client-detail__grid">
        {/* Contact card — click anywhere (except the email/phone links) to open
            the Edit client drawer. */}
        <section
          className="client-card client-card--editable"
          role="button"
          tabIndex={0}
          aria-label="Edit client"
          onClick={() => setEditing(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setEditing(true);
            }
          }}
        >
          <div className="client-card__head">
            <h2>Contact</h2>
            <span className="client-card__edit" aria-hidden="true">Edit</span>
          </div>
          <dl className="client-kv">
            {client.is_company && client.contact_role ? <KV label="Role">{client.contact_role}</KV> : null}
            <KV label="Email">{client.email ? <a href={`mailto:${client.email}`} onClick={(e) => e.stopPropagation()}>{client.email}</a> : "—"}</KV>
            <KV label="Phone">{client.phone ? <a href={`tel:${client.phone}`} onClick={(e) => e.stopPropagation()}>{client.phone}</a> : "—"}</KV>
            <KV label="Address">{composedAddress || "—"}</KV>
            <KV label="Postcode"><span className="numeric">{client.postcode || "—"}</span></KV>
          </dl>
          {client.notes ? (
            <div className="client-card__notes">
              <h3>Notes</h3>
              <p>{client.notes}</p>
            </div>
          ) : null}
        </section>

        {/* Financials */}
        <section className="client-card">
          <h2>Financials</h2>
          <div className="client-stats">
            <Stat label="Total invoiced" value={formatMoney(financials.total_invoiced)} />
            <Stat label="Outstanding" value={formatMoney(financials.outstanding)} tone={financials.outstanding > 0 ? "warn" : undefined} />
            <Stat label="Overdue" value={formatMoney(financials.overdue)} tone={financials.overdue > 0 ? "danger" : undefined} />
          </div>
          <p className="client-card__hint">Across all of this client's jobs · outstanding net of credit notes.</p>
        </section>

        {/* Client app access. The invite email deliberately carries no sign-in
            link — see api/clients.ts invitePortalUser — so the copy tells staff
            what the client will actually have to do. */}
        <section className="client-card">
          <div className="client-card__head">
            <h2>Client app</h2>
            {portalState === "active" ? <span className="clients-badge clients-badge--ok">Active</span> : null}
            {portalState === "pending" ? <span className="clients-badge clients-badge--warn">Invited</span> : null}
            {portalState === "disabled" ? <span className="clients-badge clients-badge--warn">Access disabled</span> : null}
            {portalState === "none" ? <span className="clients-badge clients-badge--muted">No access</span> : null}
          </div>

          <dl className="client-kv">
            <KV label="Signs in with">{portal.invited ? portal.email : clientEmail || "—"}</KV>
            {portal.invited ? (
              <KV label="Invited">{portal.invited_at > 0 ? formatDateShort(portal.invited_at) : "—"}</KV>
            ) : null}
            {portalState === "active" ? (
              <KV label="Last signed in">{portal.last_login_at > 0 ? formatDateShort(portal.last_login_at) : "—"}</KV>
            ) : null}
          </dl>

          <div className="client-app__action">
            <Button
              variant={portalState === "none" ? "primary" : "secondary"}
              onClick={invite}
              loading={inviting}
              disabled={inviting || !clientEmail}
              title={
                clientEmail
                  ? "Email this client the app download links. They sign in by entering their email address in the app."
                  : "Add an email address to the contact card first."
              }
            >
              {portalState === "none" ? "Invite to app" : portalState === "disabled" ? "Restore access" : "Resend invite"}
            </Button>
          </div>

          <p className="client-card__hint">
            {!clientEmail
              ? "Add an email address to the contact card before inviting."
              : portalState === "none"
                ? "Sends the app download links. There's no sign-in link in the email — they install the app, enter this address, and it emails them a code."
                : portalState === "pending"
                  ? "Invited, but hasn't signed in yet. Resending sends the download links again."
                  : portalState === "disabled"
                    ? "Access was switched off. Restoring re-enables it and re-sends the download links."
                    : "They're using the app. Resending just sends the download links again."}
            {portal.user_count > 1 ? ` This client has ${portal.user_count} people with app access.` : ""}
          </p>
        </section>
      </div>

      {/* Jobs */}
      <section className="client-card">
        <h2>Jobs <span className="clients-count">{jobs.length}</span></h2>
        {jobs.length === 0 ? (
          <p className="client-card__empty">No jobs linked to this client.</p>
        ) : (
          <ul className="client-jobs">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link to={`/staff/jobs/${j.id}`} className="client-jobs__row">
                  <span className="client-jobs__num numeric">{j.job_number || "—"}</span>
                  <span className="client-jobs__name">{j.job_name || j.address || "Untitled job"}</span>
                  <span className="client-jobs__meta">
                    {j.is_lead ? <Pill tone="info">Lead client</Pill> : null}
                    {j.status ? <JobStagePill stage={j.status} /> : <span className="client-jobs__status">—</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Open activity */}
      <div className="client-detail__grid">
        <section className="client-card">
          <h2>Open tasks <span className="clients-count">{open_tasks.length}</span></h2>
          {open_tasks.length === 0 ? (
            <p className="client-card__empty">No open tasks.</p>
          ) : (
            <ul className="client-activity">
              {open_tasks.map((t) => (
                <li key={t.id}>
                  <Link to={`/staff/jobs/${t.job_id}`} className="client-activity__row">
                    <span className="client-activity__title">{t.title}</span>
                    <span className="client-activity__meta">
                      {humanise(t.status)}{t.due_date ? ` · due ${formatDateShort(t.due_date)}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="client-card">
          <h2>Open applications <span className="clients-count">{open_applications.length}</span></h2>
          {open_applications.length === 0 ? (
            <p className="client-card__empty">No open applications.</p>
          ) : (
            <ul className="client-activity">
              {open_applications.map((a) => (
                <li key={a.id}>
                  <Link to={`/staff/jobs/${a.job_id}`} className="client-activity__row">
                    <span className="client-activity__title numeric">{a.reference || "Draft — no reference"}</span>
                    <span className="client-activity__meta">
                      {a.type === "building_control" ? "Building control" : "Planning"} · {humanise(a.status || "—")}
                      {a.local_authority_or_bcb ? ` · ${a.local_authority_or_bcb}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {editing ? (
        <EditClientDrawer
          client={asJobClient(client)}
          jobs={jobs}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="client-kv__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" | "danger" }) {
  return (
    <div className={`client-stat${tone ? ` client-stat--${tone}` : ""}`}>
      <span className="client-stat__value numeric">{value}</span>
      <span className="client-stat__label">{label}</span>
    </div>
  );
}
